import React, { useState } from 'react';
import { 
  Settings, LogOut, Package, Users, Calendar, Mail, 
  DollarSign, TrendingUp, ShoppingCart, MessageSquare, UserPlus, MapPin, Clipboard
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useAdmin } from '../../context/AdminContext';
import { useNavigate } from 'react-router-dom';
import { EnhancedTireManagement } from './EnhancedTireManagement';
import { BusinessInfoManager } from './BusinessInfoManager';
import { OrdersView } from './OrdersView';
import { BookingsView } from './BookingsView';
import { MembersView } from './MembersView';
import { ContactMessagesView } from './ContactMessagesView';
import { ProviderManagement } from './ProviderManagement';
import { RoadsideManagement } from './RoadsideManagement';
import { ApplicationManagement } from './ApplicationManagement';

export const AdminDashboard = () => {
  const { logout } = useAdmin();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                <Settings className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-sm text-gray-600">Vargas Tire & Auto Repair</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white p-1 border border-gray-200">
            <TabsTrigger value="overview" className="gap-2">
              <TrendingUp className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="roadside" className="gap-2">
              <MapPin className="w-4 h-4" />
              Roadside
            </TabsTrigger>
            <TabsTrigger value="tires" className="gap-2">
              <Package className="w-4 h-4" />
              Tires
            </TabsTrigger>
            <TabsTrigger value="orders" className="gap-2">
              <ShoppingCart className="w-4 h-4" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="bookings" className="gap-2">
              <Calendar className="w-4 h-4" />
              Bookings
            </TabsTrigger>
            <TabsTrigger value="members" className="gap-2">
              <Users className="w-4 h-4" />
              Members
            </TabsTrigger>
            <TabsTrigger value="providers" className="gap-2">
              <UserPlus className="w-4 h-4" />
              Providers
            </TabsTrigger>
            <TabsTrigger value="applications" className="gap-2">
              <Clipboard className="w-4 h-4" />
              Applications
            </TabsTrigger>
            <TabsTrigger value="messages" className="gap-2">
              <MessageSquare className="w-4 h-4" />
              Messages
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2">
              <Settings className="w-4 h-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Orders</p>
                    <p className="text-3xl font-bold text-gray-900">--</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <ShoppingCart className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Members</p>
                    <p className="text-3xl font-bold text-gray-900">--</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Bookings</p>
                    <p className="text-3xl font-bold text-gray-900">--</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Messages</p>
                    <p className="text-3xl font-bold text-gray-900">--</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </Card>
            </div>

            <Card className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  onClick={() => setActiveTab('tires')}
                  className="h-20 bg-red-600 hover:bg-red-700 text-white"
                >
                  <Package className="w-5 h-5 mr-2" />
                  Manage Tires
                </Button>
                <Button
                  onClick={() => setActiveTab('orders')}
                  variant="outline"
                  className="h-20 border-2"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  View Orders
                </Button>
                <Button
                  onClick={() => setActiveTab('settings')}
                  variant="outline"
                  className="h-20 border-2"
                >
                  <Settings className="w-5 h-5 mr-2" />
                  Business Settings
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* Tires Tab */}
          <TabsContent value="tires">
            <EnhancedTireManagement />
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <OrdersView />
          </TabsContent>

          {/* Roadside Tab */}
          <TabsContent value="roadside">
            <RoadsideManagement />
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <BookingsView />
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members">
            <MembersView />
          </TabsContent>

          {/* Providers Tab */}
          <TabsContent value="providers">
            <ProviderManagement />
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <ApplicationManagement />
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <ContactMessagesView />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <BusinessInfoManager />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
